/* Copyright (c) Trimble Inc. All rights reserved. */

import type { PKCEPair } from '../models/PKCE.model';
import { PKCE_CODE_VERIFIER_STORAGE_KEY } from '../config/CONSTANTS';
import { Logger, Persistor } from '@tcp-base/toolkit';

/**
 * PKCE (Proof Key for Code Exchange) Service
 *
 * Generates and manages PKCE code verifiers and challenges with chaining logic.
 * Each call returns the previous verifier (for token exchange) and new challenge (for authorization).
 * This ensures continuity between OAuth flows and prevents authorization code interception.
 */

class PKCEGenerationHelperImpl {
  constructor() {
    //  constructor to prevent instantiation
  }
  /**
   * Base64 URL encode without padding
   */
  public base64URLEncode = (buffer: Uint8Array): string => {
    const base64 = btoa(String.fromCharCode(...buffer));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); // Remove padding
  };

  /**
   * Generate cryptographically secure random code verifier
   * Per RFC 7636: 43-128 characters, URL-safe without padding
   */
  public generateCodeVerifier = (): string => {
    const array = new Uint8Array(32); // 32 bytes = 43 characters when base64url encoded
    crypto.getRandomValues(array);
    return this.base64URLEncode(array);
  };

  /**
   * Generate SHA256 code challenge from verifier
   * Per RFC 7636: SHA256(verifier) -> base64url encode
   */
  public generateCodeChallenge = async (verifier: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64URLEncode(new Uint8Array(digest));
  };

  /**
   * Store code verifier in session storage
   */
  private storeCodeVerifier = (verifier: string): void => {
    try {
      Persistor.setItem(PKCE_CODE_VERIFIER_STORAGE_KEY, verifier);
    } catch (error) {
      Logger.warn('PKCE Service: Failed to store code verifier in session storage:', error);
    }
  };

  /**
   * Retrieve code verifier from session storage
   */
  private getStoredCodeVerifier = (): string | null => {
    try {
      return Persistor.getItem(PKCE_CODE_VERIFIER_STORAGE_KEY);
    } catch (error) {
      Logger.warn('PKCE Service: Failed to retrieve code verifier from session storage:', error);
      return null;
    }
  };

  /**
   * Remove code verifier from session storage
   */
  private clearStoredCodeVerifier = (): void => {
    try {
      Persistor.removeItem(PKCE_CODE_VERIFIER_STORAGE_KEY);
    } catch (error) {
      Logger.warn('PKCE Service: Failed to clear code verifier from session storage:', error);
    }
  };

  /**
   * Generate PKCE pair with chaining logic using session storage
   *
   * Returns:
   * - previousCodeVerifier: Use this for token exchange (completing current flow)
   * - newCodeChallenge: Use this for authorization URL (starting next flow)
   * - codeChallengeMethod: Always 'S256' per PKCE spec
   */
  public generate = async (): Promise<PKCEPair> => {
    try {
      // Get the previous verifier from session storage for chaining
      const previousCodeVerifier = this.getStoredCodeVerifier();

      // Generate new verifier for the chain
      const newCodeVerifier = this.generateCodeVerifier();

      // Generate challenge from new verifier
      const newCodeChallenge = await this.generateCodeChallenge(newCodeVerifier);

      // Store new verifier in session storage for next call
      this.storeCodeVerifier(newCodeVerifier);

      // Return chaining result
      return {
        previousCodeVerifier, // null on first call, then has stored value
        newCodeChallenge, // always has new challenge
        codeChallengeMethod: 'S256',
      };
    } catch (error) {
      Logger.error('PKCE Service: Failed to generate PKCE pair:', error);
      throw new Error('Failed to generate PKCE codes. Ensure crypto.subtle is available.');
    }
  };

  /**
   * Reset the PKCE chain (clears stored verifier from session storage)
   * Useful for testing or manual cleanup
   */
  public reset = (): void => {
    this.clearStoredCodeVerifier();
  };

  /**
   * Check if there's a previous verifier available in session storage
   */
  public hasPreviousVerifier = (): boolean => {
    return this.getStoredCodeVerifier() !== null;
  };

  /**
   * Get current verifier from session storage (for debugging - use carefully)
   */
  public getCurrentVerifier = (): string | null => {
    return this.getStoredCodeVerifier();
  };
}

// Export singleton instance
export const PKCEGenerationHelper = new PKCEGenerationHelperImpl();

/**
 * Standalone utility functions (if needed outside of service)
 */
export default PKCEGenerationHelper;
